import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { parseEntityRef } from '@backstage/catalog-model';
import { TextField, Typography, makeStyles } from '@material-ui/core';
import { useInsights } from '../catalog/pipeline/useInsights';
import { updatedLabel } from '../home/updatedLabel';
import { plural } from '../home/attention';
import { CollapsedNotices } from '../scorecards/CollapsedNotices';
import { entityPageOf } from '../scorecards/scorecards';
import { visuallyHidden } from '../visuallyHidden';
import {
  ACCESS_PAGE,
  ageOf,
  isNoTeam,
  isOpen,
  MARK_COLOUR,
  MARK_SHAPE,
  SEVERITY_COLOUR,
  SEVERITY_SHAPE,
  MARK_WORD,
  noticesSummary,
  securityTabOf,
  SEVERITIES,
  severityCounts,
  sourceNotice,
  unmeasuredReasonOf,
  unreportedNotice,
  VERDICT_MARKS,
  worstOf,
  type ComponentSecurity,
  type Layer,
  type LayerId,
  type SecuritySnapshot,
} from './security';
import {
  FindingsTable,
  LayerCellView,
  NotMeasured,
  Shape,
  VerdictCounts,
} from './SecurityParts';

/** How many of a row's findings it lists before leading to the tab. */
const LISTED_PER_ROW = 5;

// The choices that narrow nothing, as on Scorecards.
const ALL = '';
const NO_TEAM = '-';
const MY_TEAMS = 'mine';

/** The outcomes read from the open findings' severities. */
type BySeverity = 'critical' | 'high' | 'open';
type Outcome = typeof ALL | BySeverity | 'none' | 'unmeasured';
type Order = 'severity' | 'age' | 'name';
type FilterValues = {
  query: string;
  team: string;
  layer: typeof ALL | LayerId;
  outcome: Outcome;
};

/** The least severe finding each outcome keeps, by its rank in SEVERITIES. */
const LEAST_SEVERE: Record<BySeverity, number> = {
  critical: 0,
  high: 1,
  open: SEVERITIES.length - 1,
};

const useStyles = makeStyles(theme => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  figures: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflow: 'hidden',
  },
  figure: {
    padding: theme.spacing(1.5, 2),
    minWidth: 0,
    borderRight: '1px solid var(--ui-border)',
    '&:last-child': { borderRight: 'none' },
    [theme.breakpoints.down('xs')]: {
      '&:nth-child(2)': { borderRight: 'none' },
      '&:nth-child(-n+2)': { borderBottom: '1px solid var(--ui-border)' },
    },
  },
  label: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  value: {
    fontSize: '1.5rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.4,
    color: 'var(--ui-text)',
  },
  quiet: {
    fontSize: '1.1rem',
    lineHeight: 1.9,
    color: 'var(--ui-text-muted)',
  },
  sub: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  // The row of filters as on Scorecards: one row on a desktop, a long choice
  // cut short inside its field rather than widening it. Two to a row below.
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) auto',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  select: { minWidth: 0 },
  count: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    whiteSpace: 'nowrap',
  },
  // The table scrolls sideways inside its own box on a narrow screen, so the
  // page itself never does (AC-13). Positioned for the words kept for screen
  // readers, as on Scorecards.
  tableBox: {
    position: 'relative',
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflowX: 'auto',
    minWidth: 0,
  },
  table: {
    width: '100%',
    minWidth: 860,
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
    color: 'var(--ui-text)',
    '& > thead > tr > th, & > tbody > tr > th, & > tbody > tr > td': {
      padding: theme.spacing(1.25, 1.5),
      borderBottom: '1px solid var(--ui-border)',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& > thead th': {
      fontWeight: 400,
      fontSize: '0.8rem',
      color: 'var(--ui-text-muted)',
    },
  },
  componentCell: { minWidth: 200 },
  componentName: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontWeight: 600,
  },
  kind: {
    fontWeight: 400,
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    marginLeft: theme.spacing(0.5),
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: 'var(--ui-text-muted)',
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': { background: 'var(--ui-bg-muted)' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
  },
  chevron: {
    display: 'inline-block',
    transition: 'transform 120ms',
    '&[data-open="true"]': { transform: 'rotate(90deg)' },
  },
  expanded: {
    padding: theme.spacing(1, 1.5, 1.5, 5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  layerHead: {
    margin: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--ui-text-muted)',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 2),
    margin: 0,
    padding: theme.spacing(1.25, 2),
    listStyle: 'none',
    borderTop: '1px solid var(--ui-border)',
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    '& b': { color: 'var(--ui-text)', marginRight: 4 },
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    marginLeft: theme.spacing(1),
  },
  hidden: visuallyHidden,
  message: { color: 'var(--ui-text-muted)', padding: theme.spacing(2, 0) },
}));

const Figure = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.figure} role="group" aria-label={label}>
      <div className={classes.label}>{label}</div>
      {children}
    </div>
  );
};

/** The figures above the table: one per layer, never added up (AC-2). */
const Figures = ({ snapshot }: { snapshot: SecuritySnapshot }) => {
  const classes = useStyles();
  const { summary, layers, sources } = snapshot;
  const labelsOf = (ids: string[]) =>
    ids.map(id => sources.find(s => s.id === id)?.label ?? id).join(' and ');
  return (
    <section aria-label="Figures" className={classes.figures}>
      {layers.map(layer => {
        const figure = summary.layers[layer.id];
        if (figure.sources.length === 0) {
          return (
            <Figure key={layer.id} label={layer.title}>
              <div className={classes.quiet}>Not measured</div>
              <div className={classes.sub}>No source reports here yet</div>
            </Figure>
          );
        }
        // A source answered, but measured nothing in any component yet — only
        // items still waiting for an analysis. That is no "None open".
        if (figure.components === 0) {
          return (
            <Figure key={layer.id} label={layer.title}>
              <div className={classes.quiet}>Not measured</div>
              <div className={classes.sub}>
                Nothing measured yet by {labelsOf(figure.sources)}
              </div>
            </Figure>
          );
        }
        const marks = Object.keys(figure.byMark).length > 0;
        if (marks) {
          return (
            <Figure key={layer.id} label={layer.title}>
              <div className={classes.value}>
                <VerdictCounts byMark={figure.byMark} />
              </div>
              <div className={classes.sub}>
                of{' '}
                {plural(figure.measured, 'node or layer', 'nodes and layers')}{' '}
                in {plural(figure.components, 'package', 'packages')}
              </div>
            </Figure>
          );
        }
        const worst = worstOf(figure.bySeverity);
        const rest = severityCounts(figure.bySeverity).filter(
          c => c.severity !== worst,
        );
        return (
          <Figure key={layer.id} label={layer.title}>
            <div className={classes.value}>
              {worst ? `${figure.bySeverity[worst]} ${worst}` : 'None open'}
            </div>
            <div className={classes.sub}>
              {rest.map(c => `${c.count} ${c.severity}`).join(' · ')}
              {rest.length > 0 ? ', from ' : 'from '}
              {labelsOf(figure.sources)}
            </div>
          </Figure>
        );
      })}
      <Figure label="Measured">
        <div className={classes.value}>
          {summary.measured} of {summary.components}
        </div>
        <div className={classes.sub}>
          components read by at least one source
        </div>
      </Figure>
    </section>
  );
};

/** A component by its title, or by its name when it has none. */
const nameOf = (row: ComponentSecurity) => row.title ?? row.name;

/** A row's open findings, under the layer each belongs to (AC-8). */
const RowFindings = ({
  row,
  snapshot,
  now,
}: {
  row: ComponentSecurity;
  snapshot: SecuritySnapshot;
  now: number;
}) => {
  const classes = useStyles();
  const open = row.findings.filter(isOpen);
  const listed = open.slice(0, LISTED_PER_ROW);
  const more = open.length - listed.length;
  const name = nameOf(row);
  const unread = row.readings.filter(r => r.state === 'unmeasured');
  return (
    <div className={classes.expanded}>
      {snapshot.layers.map(layer =>
        snapshot.sources
          .filter(source => source.layer === layer.id)
          .map(source => {
            const findings = listed.filter(f => f.source === source.id);
            if (findings.length === 0) return null;
            return (
              <div key={source.id}>
                <h3 className={classes.layerHead}>
                  {layer.title} · {source.label}
                </h3>
                <FindingsTable
                  source={source}
                  findings={findings}
                  now={now}
                  label={`${layer.title} findings of ${name}`}
                />
              </div>
            );
          }),
      )}
      {/* A source can read a component and still have measured nothing of it
          — items not analysed yet — which is not measured, never "nothing". */}
      {open.length === 0 &&
        unread.length === 0 &&
        (row.status === 'none' ? (
          <Typography className={classes.sub}>Nothing is open.</Typography>
        ) : (
          <Typography className={classes.sub}>
            <NotMeasured reasons={[]} /> — {unmeasuredReasonOf(row)}
          </Typography>
        ))}
      {unread.map(r => (
        <Typography key={r.source} className={classes.sub}>
          {snapshot.sources.find(s => s.id === r.source)?.label ?? r.source}:{' '}
          <NotMeasured reasons={[]} /> — {r.reason}
        </Typography>
      ))}
      {more > 0 && (
        <Link to={securityTabOf(row.entityRef)}>
          {plural(more, 'more', 'more')} on the component's Security tab
        </Link>
      )}
    </div>
  );
};

/** An owner by its name, or by its entity's name — never as a raw reference. */
const ownerName = (owner: { ref: string; title?: string }): string => {
  if (owner.title) return owner.title;
  try {
    return parseEntityRef(owner.ref).name;
  } catch {
    return owner.ref;
  }
};

const Row = ({
  row,
  snapshot,
  now,
}: {
  row: ComponentSecurity;
  snapshot: SecuritySnapshot;
  now: number;
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const name = nameOf(row);
  const id = `security-${row.entityRef.replace(/[^a-z0-9-]/gi, '-')}`;
  return (
    <>
      <tr>
        <th scope="row" className={classes.componentCell}>
          <div className={classes.componentName}>
            <button
              type="button"
              className={classes.toggle}
              aria-expanded={open}
              aria-controls={`${id}-findings`}
              aria-label={`The findings of ${name}`}
              onClick={() => setOpen(o => !o)}
            >
              <span className={classes.chevron} data-open={open} aria-hidden>
                ›
              </span>
            </button>
            <Link to={entityPageOf(row.entityRef)}>{name}</Link>
            {row.type && <span className={classes.kind}>{row.type}</span>}
          </div>
        </th>
        <td>{row.owner ? ownerName(row.owner) : '—'}</td>
        {snapshot.layers.map(layer => (
          <td key={layer.id}>
            <LayerCellView cell={row.layers[layer.id]} />
          </td>
        ))}
        <td>
          {row.oldestOpen ? (
            <time dateTime={row.oldestOpen} title={row.oldestOpen}>
              {ageOf(row.oldestOpen, now)}
            </time>
          ) : (
            '—'
          )}
        </td>
      </tr>
      {open && (
        <tr id={`${id}-findings`}>
          <td colSpan={snapshot.layers.length + 3}>
            <RowFindings row={row} snapshot={snapshot} now={now} />
          </td>
        </tr>
      )}
    </>
  );
};

/** A component's owner, when it is a team: the team filter offers these. */
const teamOf = (row: ComponentSecurity) =>
  row.owner && /^group:/i.test(row.owner.ref) ? row.owner : undefined;

/** Whether a row belongs to the owner team picked, or to the reader's own. */
function ownedBy(
  row: ComponentSecurity,
  team: string,
  mine: Set<string>,
): boolean {
  switch (team) {
    case ALL:
      return true;
    // A team, as the choice says: a component its reader owns in person has
    // no team as owner, and only that choice keeps it.
    case MY_TEAMS: {
      const owner = teamOf(row);
      return Boolean(owner && mine.has(owner.ref.toLocaleLowerCase('en-US')));
    }
    case NO_TEAM:
      return !teamOf(row);
    default:
      return teamOf(row)?.ref === team;
  }
}

/**
 * Whether a row stays under the filters a colleague picked (AC-7). A layer on
 * its own keeps the components it covers; an outcome is read in that layer,
 * or in every layer when none is picked. `query` comes trimmed and in lower
 * case, once for every row.
 */
function matches(
  row: ComponentSecurity,
  { query, team, layer, outcome }: FilterValues,
  mine: Set<string>,
): boolean {
  if (
    query &&
    !`${row.title ?? ''} ${row.name}`.toLocaleLowerCase('en-US').includes(query)
  ) {
    return false;
  }
  if (!ownedBy(row, team, mine)) return false;
  const cells = layer === ALL ? Object.values(row.layers) : [row.layers[layer]];
  if (layer !== ALL && cells[0].state === 'uncovered') return false;
  switch (outcome) {
    case ALL:
      return true;
    // Across the layers the backend's own word counts as well, as it does for
    // none open, so a component it calls not measured is never left out of
    // both.
    case 'unmeasured':
      return (
        (layer === ALL && row.status === 'unmeasured') ||
        cells.some(cell => cell.state === 'unmeasured')
      );
    // Nothing open where it was read, and nothing left unread. Across the
    // layers that is the backend's own word for the component, so a layer one
    // source read and another could not never makes it look clean.
    case 'none':
      return layer === ALL
        ? row.status === 'none'
        : cells[0].state === 'read' && cells[0].open === 0;
    default:
      return row.findings.some(
        f =>
          isOpen(f) &&
          (layer === ALL || f.layer === layer) &&
          SEVERITIES.indexOf(f.severity!) <= LEAST_SEVERE[outcome],
      );
  }
}

/**
 * When a row's oldest open finding opened; one with none, or with a date that
 * does not read, sorts after all.
 */
const openSinceOf = (row: ComponentSecurity) => {
  const since = row.oldestOpen ? Date.parse(row.oldestOpen) : NaN;
  return Number.isNaN(since) ? Infinity : since;
};

/** The teams the reader belongs to, for "My teams". */
function useMyRefs(): Set<string> {
  const identityApi = useApi(identityApiRef);
  const [refs, setRefs] = useState<Set<string>>(new Set());
  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (active) {
          setRefs(
            new Set(
              identity.ownershipEntityRefs.map(r =>
                r.toLocaleLowerCase('en-US'),
              ),
            ),
          );
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [identityApi]);
  return refs;
}

/**
 * Narrowing and ordering the rows (AC-7), as Scorecards does: by the
 * component's name, its owning team, a layer and what was found in it — and
 * the most severe or the oldest open finding first instead of by name. The
 * figures and the notices stay the catalog's whole; only the rows narrow.
 */
const Filters = ({
  teams,
  layers,
  values,
  order,
  shown,
  total,
  onChange,
  onOrder,
}: {
  teams: { ref: string; label: string; count: number }[];
  /** The layers a source reports; picking another would always be empty. */
  layers: Layer[];
  values: FilterValues;
  order: Order;
  shown: number;
  total: number;
  onChange: (values: FilterValues) => void;
  onOrder: (order: Order) => void;
}) => {
  const classes = useStyles();
  const set = (change: Partial<FilterValues>) =>
    onChange({ ...values, ...change });
  return (
    <div className={classes.filters} role="group" aria-label="Filters">
      <TextField
        id="security-component-filter"
        size="small"
        variant="outlined"
        label="Component"
        placeholder="Name"
        value={values.query}
        onChange={e => set({ query: e.target.value })}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      />
      <TextField
        id="security-team-filter"
        select
        size="small"
        variant="outlined"
        label="Owner team"
        value={values.team}
        onChange={e => set({ team: e.target.value })}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      >
        <option value={ALL}>All teams</option>
        <option value={MY_TEAMS}>My teams</option>
        {teams.map(t => (
          <option key={t.ref} value={t.ref}>
            {t.label} ({t.count})
          </option>
        ))}
        <option value={NO_TEAM}>No team as owner</option>
      </TextField>
      <TextField
        id="security-layer-filter"
        select
        size="small"
        variant="outlined"
        label="Layer"
        value={values.layer}
        onChange={e => set({ layer: e.target.value as FilterValues['layer'] })}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      >
        <option value={ALL}>All layers</option>
        {layers.map(l => (
          <option key={l.id} value={l.id}>
            {l.title}
          </option>
        ))}
      </TextField>
      <TextField
        id="security-outcome-filter"
        select
        size="small"
        variant="outlined"
        label="Findings"
        value={values.outcome}
        onChange={e => set({ outcome: e.target.value as Outcome })}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      >
        <option value={ALL}>Any outcome</option>
        <option value="critical">Critical</option>
        <option value="high">High and above</option>
        <option value="open">Any open</option>
        <option value="none">None open</option>
        <option value="unmeasured">Not measured</option>
      </TextField>
      <TextField
        id="security-order"
        select
        size="small"
        variant="outlined"
        label="Order"
        value={order}
        onChange={e => onOrder(e.target.value as Order)}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      >
        <option value="name">By name</option>
        <option value="severity">Most severe first</option>
        <option value="age">Oldest open first</option>
      </TextField>
      <Typography className={classes.count} aria-live="polite">
        {shown === total
          ? plural(total, 'component', 'components')
          : `${shown} of ${plural(total, 'component', 'components')}`}
      </Typography>
    </div>
  );
};

const Findings = ({ snapshot }: { snapshot: SecuritySnapshot }) => {
  const classes = useStyles();
  const mine = useMyRefs();
  const [filters, setFilters] = useState<FilterValues>({
    query: '',
    team: ALL,
    layer: ALL,
    outcome: ALL,
  });
  const [order, setOrder] = useState<Order>('name');
  const now = Date.now();
  const teams = useMemo(() => {
    const byRef = new Map<
      string,
      { ref: string; label: string; count: number }
    >();
    for (const row of snapshot.components) {
      const owner = teamOf(row);
      if (!owner) continue;
      const known = byRef.get(owner.ref);
      if (known) known.count += 1;
      else
        byRef.set(owner.ref, {
          ref: owner.ref,
          label: ownerName(owner),
          count: 1,
        });
    }
    return [...byRef.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }),
    );
  }, [snapshot.components]);
  const shown = useMemo(() => {
    const wanted = {
      ...filters,
      query: filters.query.trim().toLocaleLowerCase('en-US'),
    };
    const narrowed = snapshot.components.filter(row =>
      matches(row, wanted, mine),
    );
    // The backend orders the most severe first: open findings by their worst
    // and how many, then what nobody could read, then what is clean, equals
    // by name. By name is the page's own order, as on Scorecards, and the
    // oldest open finding first keeps it among equals.
    if (order === 'severity') return narrowed;
    const byName = [...narrowed].sort((a, b) =>
      nameOf(a).localeCompare(nameOf(b), 'en', { sensitivity: 'base' }),
    );
    // Two with nothing open are equal: Infinity - Infinity is no number.
    return order === 'age'
      ? byName.sort((a, b) => openSinceOf(a) - openSinceOf(b) || 0)
      : byName;
  }, [snapshot.components, filters, order, mine]);
  // The snapshot's own, whatever is typed into the filters.
  const { reported, notices, summary } = useMemo(() => {
    const withSource = snapshot.layers.filter(l =>
      snapshot.sources.some(s => s.layer === l.id),
    );
    const unreported = snapshot.layers.filter(l => !withSource.includes(l));
    return {
      reported: withSource,
      notices: [
        ...snapshot.sources
          .map(source => sourceNotice(source, snapshot.components))
          .filter((n): n is string => Boolean(n)),
        ...unreported.map(unreportedNotice),
      ],
      summary: noticesSummary(snapshot.sources, unreported),
    };
  }, [snapshot]);

  return (
    <div className={classes.stack}>
      <Figures snapshot={snapshot} />
      <CollapsedNotices
        id="security-not-counted"
        label="Not counted"
        summary={summary}
        notices={notices}
      />
      {snapshot.components.length > 0 && (
        <Filters
          teams={teams}
          layers={reported}
          values={filters}
          order={order}
          shown={shown.length}
          total={snapshot.components.length}
          onChange={setFilters}
          onOrder={setOrder}
        />
      )}
      {snapshot.components.length === 0 ? (
        <Typography className={classes.message}>
          The catalog holds no component that names its repository.
        </Typography>
      ) : (
        <div className={classes.tableBox}>
          <table className={classes.table} aria-label="Security findings">
            <thead>
              <tr>
                <th scope="col">Component</th>
                <th scope="col">Owner</th>
                {snapshot.layers.map(l => (
                  <th key={l.id} scope="col">
                    {l.id === 'threat' ? 'Threat analysis' : l.title}
                  </th>
                ))}
                <th scope="col">Oldest open</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(row => (
                <Row
                  key={row.entityRef}
                  row={row}
                  snapshot={snapshot}
                  now={now}
                />
              ))}
            </tbody>
          </table>
          {shown.length === 0 && (
            <Typography className={classes.message} style={{ padding: 16 }}>
              No component matches these filters.
            </Typography>
          )}
          <ul className={classes.legend} aria-label="Legend">
            <li>
              <b>Dependencies</b>the advisory's severity:
              {SEVERITIES.map(level => (
                <span key={level} className={classes.legendItem}>
                  <Shape
                    shape={SEVERITY_SHAPE[level]}
                    colour={SEVERITY_COLOUR[level]}
                  />
                  {level}
                </span>
              ))}
            </li>
            <li>
              <b>Threat analysis</b>the verdict of each node's newest report:
              {VERDICT_MARKS.map(mark => (
                <span key={mark} className={classes.legendItem}>
                  <Shape shape={MARK_SHAPE[mark]} colour={MARK_COLOUR[mark]} />
                  {MARK_WORD[mark]}
                </span>
              ))}
            </li>
            <li>
              <b>? Not measured</b>a source could not read it, never counted as
              0
            </li>
            <li>
              <b>—</b>no source covers this component in this layer
            </li>
          </ul>
        </div>
      )}
      <Typography className={classes.sub}>
        Read{' '}
        <time dateTime={snapshot.generatedAt!} title={snapshot.generatedAt!}>
          {updatedLabel(snapshot.generatedAt!)}
        </time>
        . Where each finding comes from, and how to clear it:{' '}
        <Link to="/docs/default/component/backstage/security-findings/">
          Security findings
        </Link>
        .
      </Typography>
    </div>
  );
};

/**
 * Security (DX-587; promised in specs/security-findings.md): what is open
 * against each component's security, one layer per column — dependencies from
 * Dependabot, threat analyses from the node security analyses, and code
 * patterns once a scan reports here.
 *
 * Nothing is counted here. The github-insights backend reads the sources every
 * hour and this page shows what it answered, so a component's own Security tab
 * and facts bar, which read the same answer, cannot count differently. What a
 * source could not read says so; it is never a 0, and never green.
 */
export const SecurityPage = () => {
  const classes = useStyles();
  const snapshot = useInsights<SecuritySnapshot>('/security', {
    refused: isNoTeam,
  });

  let body: JSX.Element;
  if (snapshot.loading) {
    body = <Progress />;
  } else if (snapshot.refused) {
    body = (
      <Typography className={classes.message}>
        Security findings are shown to members of a team, and your sign-in names
        none the catalog holds. <Link to={ACCESS_PAGE}>How to join a team</Link>
      </Typography>
    );
  } else if (snapshot.error || !snapshot.data) {
    body = (
      <Typography role="alert">
        Couldn't load the security findings from github-insights.
      </Typography>
    );
  } else if (snapshot.data.generatedAt === null) {
    body = (
      <Typography className={classes.message}>
        The findings have not been read yet since the portal started. They are
        read every hour; check back in a moment.
      </Typography>
    );
  } else {
    body = <Findings snapshot={snapshot.data} />;
  }

  return (
    <Page themeId="home">
      <Header
        title="Security"
        subtitle="Open findings across the catalog, by layer. Dependencies, code patterns and threat analyses are counted apart."
      />
      <Content>{body}</Content>
    </Page>
  );
};
